import { useLayoutEffect } from 'react'

import SkeletonGroup, { type SkeletonGroupProps } from '@/components/motion/SkeletonGroup'
import Skeleton from '@/components/ui/Skeleton'
import { dismissBootSplash } from '@/lib/boot-splash'

/**
 * The chart's loading shape, laid out the way TradingView lays itself out: a
 * toolbar strip over the top, a tool rail down the left, and a plot with its
 * price scale on the right and its time axis along the foot. When the library
 * mounts it fills the same box, so nothing moves.
 *
 * Fills its parent (`size-full`) rather than carrying a height: the chart
 * region is the whole viewport, as the chart is full-screen. The tool rail and
 * the price scale show at every width: the library keeps both on a phone too
 * (it hides the header and rail only when the chart is short, as on a
 * landscape phone), so leaving them out would move the plot when it mounts.
 *
 * `appear` is the 80ms wait and fade the blocks carry, for a skeleton drawn
 * on its own. The overlay `TradingChart` paints over a skeleton the route's
 * pending view already showed passes `false`: a fresh entrance there would
 * blank the blocks and fade them back in the same frame.
 *
 * Decorative, so the whole thing is one polite status for assistive tech
 * rather than forty unlabelled blocks.
 */
export default function ChartSkeleton({ appear = true }: { appear?: boolean }) {
  // This one is the same shape as index.html's static splash, so it can take
  // over from it before the browser paints.
  useLayoutEffect(dismissBootSplash, [])
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading the chart"
      className="flex size-full min-h-0 flex-col bg-background"
    >
      <Group
        appear={appear}
        className="flex h-10 shrink-0 items-center gap-3 border-b border-border px-3"
      >
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-16" />
        <Skeleton className="hidden h-6 w-20 md:block" />
        <Skeleton className="hidden h-6 w-24 md:block" />
        <Skeleton className="ml-auto h-6 w-8" />
      </Group>

      <div className="flex min-h-0 flex-1">
        <Group
          appear={appear}
          className="flex w-14 shrink-0 flex-col items-center gap-3 border-r border-border py-3"
        >
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="size-8" />
          ))}
        </Group>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1">
            <Group appear={appear} className="flex min-w-0 flex-1 items-end gap-2 p-4">
              {/* Bars of uneven height read as a price series, not a
                  loading bar. Fixed, so the shape does not shuffle. */}
              {BAR_HEIGHTS.map((height, index) => (
                <Skeleton key={index} className={`flex-1 ${height}`} />
              ))}
            </Group>
            <Group
              appear={appear}
              className="flex w-16 shrink-0 flex-col justify-between border-l border-border py-4 pl-2"
            >
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-3 w-10" />
              ))}
            </Group>
          </div>
          <Group
            appear={appear}
            className="flex h-8 shrink-0 items-center justify-between gap-4 border-t border-border px-4"
          >
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-3 w-12" />
            ))}
          </Group>
        </div>
      </div>
    </div>
  )
}

/** SkeletonGroup, or the same element without its entrance. */
function Group({ appear, ...rest }: SkeletonGroupProps<'div'> & { appear: boolean }) {
  return appear ? <SkeletonGroup {...rest} /> : <div {...rest} />
}

const BAR_HEIGHTS = [
  'h-1/3',
  'h-2/5',
  'h-1/2',
  'h-2/5',
  'h-3/5',
  'h-1/2',
  'h-2/3',
  'h-3/5',
  'h-1/2',
  'h-2/3',
  'h-3/4',
  'h-2/3',
] as const
